import type { ReactNode } from 'react';
import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { MenuItem, TextField } from '@mui/material';
import type { FieldBaseProps } from './types';

export interface SelectOption {
  value: string | number;
  label: ReactNode;
}

export interface FieldSelectProps<T extends FieldValues> extends FieldBaseProps<T> {
  options: SelectOption[];
  /** Placeholder rendered as a disabled empty option (translated copy). */
  placeholder?: string;
}

/** RHF-bound single select on MUI `TextField select`. */
export function FieldSelect<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  required,
  disabled,
  options,
  placeholder,
}: FieldSelectProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          size="small"
          value={field.value ?? ''}
          select
          label={label}
          required={required}
          disabled={disabled}
          fullWidth
          error={!!fieldState.error}
          helperText={fieldState.error?.message ?? helperText}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              {placeholder}
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={String(option.value)} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
}
